(() => {
  if (window.VezeloReviews) {
    window.VezeloReviews.init(document);
    return;
  }

  const instances = new Map();

  class ReviewStrip {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('[data-review-viewport]');
      this.track = section.querySelector('[data-review-track]');
      this.set = section.querySelector('[data-review-set]');
      this.controls = section.querySelector('[data-review-controls]');
      this.toggle = section.querySelector('[data-review-toggle]');
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.speed = Math.max(12, Math.min(48, Number(section.dataset.reviewSpeed) || 24));
      this.autoplay = section.dataset.reviewAutoplay === 'true';
      this.period = 0;
      this.offset = 0;
      this.frame = null;
      this.lastTime = null;
      this.userPaused = false;
      this.inView = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      section.querySelector('[data-review-previous]').addEventListener('click', () => this.move(-1), options);
      section.querySelector('[data-review-next]').addEventListener('click', () => this.move(1), options);
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
        this.toggle.textContent = this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
        this.update();
      }, options);
      this.viewport.addEventListener('keydown', (event) => {
        if (event.target !== this.viewport || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.media.addEventListener('change', () => this.measure(), options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.inView = entries.some((entry) => entry.isIntersecting);
        this.update();
      }, { rootMargin: '100px' });
      this.intersectionObserver.observe(section);
      this.controls.hidden = false;
      this.measure();
    }

    measure() {
      const previousPeriod = this.period;
      const previousPosition = previousPeriod ? this.offset / previousPeriod : 0;
      this.track.querySelectorAll('[data-review-clone]').forEach((clone) => clone.remove());
      this.animated = this.autoplay && !this.media.matches && this.set.children.length > 1;
      this.section.dataset.reviewAnimated = String(this.animated);
      this.toggle.hidden = !this.animated;
      this.period = this.set.getBoundingClientRect().width;
      this.offset = previousPosition * this.period;
      if (this.animated && this.period > 0) {
        this.viewport.scrollLeft = 0;
        const copies = Math.max(1, Math.ceil(this.viewport.clientWidth / this.period));
        for (let index = 0; index < copies; index += 1) {
          const clone = this.set.cloneNode(true);
          clone.removeAttribute('data-review-set');
          clone.setAttribute('data-review-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          for (const node of [clone, ...clone.querySelectorAll('*')]) {
            node.removeAttribute('id');
            [...node.attributes].filter((attribute) => attribute.name.startsWith('data-shopify')).forEach((attribute) => node.removeAttribute(attribute.name));
            if (node.matches('a, button, input, select, textarea, [tabindex]')) {
              node.setAttribute('tabindex', '-1');
              node.removeAttribute('href');
              if ('disabled' in node) node.disabled = true;
            }
          }
          this.track.append(clone);
        }
      }
      this.paint();
      this.update();
    }

    paint() {
      this.track.style.transform = this.animated ? `translate3d(${-this.offset}px, 0, 0)` : '';
    }

    move(direction) {
      const card = this.set.firstElementChild;
      if (!card) return;
      const distance = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.set).columnGap) || 0);
      if (this.animated && this.period > 0) {
        this.offset = ((this.offset + direction * distance) % this.period + this.period) % this.period;
        this.paint();
      } else {
        this.viewport.scrollBy({ left: direction * distance, behavior: this.media.matches ? 'auto' : 'smooth' });
      }
    }

    update() {
      const paused = this.userPaused || document.hidden || !this.inView;
      this.section.dataset.reviewState = this.animated ? (paused ? 'paused' : 'playing') : 'static';
      if (!this.animated || paused || this.period <= 0) {
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
        return;
      }
      if (this.frame !== null) return;
      const tick = (time) => {
        if (this.lastTime !== null) this.offset = (this.offset + this.speed * Math.min(0.1, (time - this.lastTime) / 1000)) % this.period;
        this.lastTime = time;
        this.paint();
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    destroy() {
      this.abort.abort();
      this.resizeObserver.disconnect();
      this.intersectionObserver.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.track.querySelectorAll('[data-review-clone]').forEach((clone) => clone.remove());
      this.track.style.transform = '';
      this.section.dataset.reviewAnimated = 'false';
      this.controls.hidden = true;
    }
  }

  const init = (root) => {
    const sections = [...root.querySelectorAll('[data-review-strip]')];
    if (root.matches?.('[data-review-strip]')) sections.unshift(root);
    sections.forEach((section) => {
      if (!instances.has(section) && getComputedStyle(section).display !== 'none') instances.set(section, new ReviewStrip(section));
    });
  };
  window.VezeloReviews = { init };
  document.addEventListener('shopify:section:load', () => init(document));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [section, instance] of instances) {
      if (event.target.contains(section)) {
        instance.destroy();
        instances.delete(section);
      }
    }
  });
  init(document);
})();
